import React, { useState } from "react";
import { OpcCard } from "../../_shared/OpcCard/OpcCard";
import { CHANGE_SOURCE, STATUS_META } from "../../_config/okrPlansCoachConfig";
import { firstNameOf, weekday } from "../../_config/okrPlansCoachFormat";
import OpcShowMore, {
  OPC_PREVIEW,
} from "../../_shared/OpcShowMore/OpcShowMore";
import "./_change-log.scss";

// ═══════════════════════════════════════════════════════════════════════════
// Change log (BE 2.7, CD24, CD32 revised): what changed on the objective
// after the lock, newest first, grouped by day. Each change is a compact
// block — target chip + statement · the change as a pill (% with a delta,
// status labels, or "Text edited") · who · where · when.
// ═══════════════════════════════════════════════════════════════════════════

const timeOf = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const isNum = (v) =>
  v !== null && v !== "" && v !== undefined && !Number.isNaN(Number(v));

// "Key result 1 "New buyers…"" → { num: 1, kr: true, text: "New buyers…" }
// (CD51: shown as the number badge — its place in the plan's list, 1, 2, 3…;
// teal for key results, grey for initiatives — never "KR1" / "I1").
// The full statement comes from the plan when the id matches.
const targetOf = (c, plan) => {
  if (c.target_type === "OBJECTIVE_STATUS")
    return { short: "Status", text: "Objective status" };
  const label = String(c.target_label || "");
  const m = label.match(/^(Key result|Initiative)\s+(\d+)\s*"?(.*?)"?$/i);
  const isKr =
    c.target_type === "KR" || (m && m[1].toLowerCase() === "key result");
  const list = isKr ? plan?.key_results : plan?.initiatives;
  const idKey = isKr ? "key_result_id" : "initiative_id";
  const item = (list || []).find(
    (x) => String(x[idKey]) === String(c.target_id),
  );
  const sorted = (list || [])
    .slice()
    .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  const at = item ? sorted.indexOf(item) : -1;
  return {
    num: at >= 0 ? at + 1 : m ? Number(m[2]) : null,
    kr: isKr,
    text: item?.statement || (m ? m[3] : label),
  };
};

// The change pill: status, progress (with delta) or a text edit.
const ChangeValue = ({ c }) => {
  const statusLike =
    c.target_type === "OBJECTIVE_STATUS" ||
    STATUS_META[c.old_value] ||
    STATUS_META[c.new_value];
  if (statusLike) {
    const from = STATUS_META[c.old_value]?.label || "Not started";
    const to = STATUS_META[c.new_value]?.label || "Not started";
    const tone = STATUS_META[c.new_value]?.tone || "muted";
    return (
      <span className="opc-log__pill">
        <span className="opc-log__from">{from}</span>
        <i className="fa-solid fa-arrow-right" aria-hidden="true" />
        <span className={`opc-log__to is-${tone}`}>{to}</span>
      </span>
    );
  }
  if (isNum(c.old_value) || isNum(c.new_value)) {
    const from = isNum(c.old_value) ? Number(c.old_value) : 0;
    const to = isNum(c.new_value) ? Number(c.new_value) : 0;
    const delta = to - from;
    return (
      <span className="opc-log__pill">
        <span className="opc-log__from">{from}%</span>
        <i className="fa-solid fa-arrow-right" aria-hidden="true" />
        <span className="opc-log__to">{to}%</span>
        {delta ? (
          <span className={`opc-log__delta ${delta > 0 ? "is-up" : "is-down"}`}>
            {delta > 0 ? `+${delta}` : `−${Math.abs(delta)}`}
          </span>
        ) : null}
      </span>
    );
  }
  if (c.old_value == null && c.new_value == null) return null;
  return (
    <span
      className="opc-log__pill opc-log__pill--text"
      title={`Before: ${c.old_value ?? "—"}\nAfter: ${c.new_value ?? "—"}`}
    >
      <i className="fa-regular fa-pen-to-square" aria-hidden="true" />
      Text edited
    </span>
  );
};

const byDay = (items) => {
  const groups = [];
  items.forEach((c) => {
    const key = String(c.changed_at || "").slice(0, 10);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(c);
    else groups.push({ key, label: weekday(c.changed_at), items: [c] });
  });
  return groups;
};

const ChangeLog = ({ log, plan, loading, onMore }) => {
  const [all, setAll] = useState(false);
  const items = log?.items || [];
  const shown = all ? items : items.slice(0, OPC_PREVIEW);

  return (
    <OpcCard
      title="Change log"
      count={items.length}
      tip="Every change to this objective after the lock — from the plan, its chats, the review or Quarterly OKRs."
    >
      {items.length ? (
        <div className="opc-log">
          {byDay(shown).map((g) => (
            <section className="opc-log__day" key={g.key || g.label}>
              <h4 className="opc-log__date">{g.label}</h4>
              <ul className="opc-log__list">
                {g.items.map((c) => {
                  const t = targetOf(c, plan);
                  return (
                    <li key={c.change_id} className="opc-log__item">
                      <div className="opc-log__target">
                        {t.short ? (
                          <span className="opc-log__chip">{t.short}</span>
                        ) : (
                          <span className={`opc-num${t.kr ? "" : " is-plain"}`}>
                            {t.num ?? "•"}
                          </span>
                        )}
                        <span className="opc-log__text" title={t.text}>
                          {t.text}
                        </span>
                      </div>
                      <div className="opc-log__line">
                        <ChangeValue c={c} />
                        <span className="opc-log__meta">
                          {[
                            firstNameOf(c.actor_name),
                            CHANGE_SOURCE[c.source] || c.source,
                            timeOf(c.changed_at),
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      ) : (
        <p className="opc-pv-empty">No changes since the lock.</p>
      )}
      {/* CD42: 3 first; "Show all N changes" ↔ "Show less". Older pages
          load only once everything here is open. */}
      {(all || items.length <= OPC_PREVIEW) && log?.next_before ? (
        <button
          type="button"
          className="opc-pv-more"
          onClick={onMore}
          disabled={loading}
        >
          {loading ? "Loading…" : "Load older changes"}
        </button>
      ) : null}
      <OpcShowMore
        total={items.length}
        open={all}
        onToggle={() => setAll((v) => !v)}
        noun="changes"
      />
    </OpcCard>
  );
};

export default ChangeLog;
